import type { LessonWords } from '../garageLesson.js';
import { el } from './dom.js';

/**
 * A lesson outside play (ADR-0041): the screen dims but for one button (or
 * the `frame` around it, when what the button is for should stay readable), a
 * gloved hand taps it, and a card says why. While it shows, that button is the
 * only thing that answers, as in the guided shifts' lessons (ADR-0030), whose
 * hand this one copies. With no button to point at, it is a card on the dimmed
 * screen, and the card's own button closes it.
 *
 * It reads nothing and decides nothing: it is told where to point and what to
 * say, and calls back when the tap lands.
 */
export interface Spotlight {
  /** Put the light back on its target, after the layout moved. */
  place(): void;
  remove(): void;
}

const SVG = 'http://www.w3.org/2000/svg';
/** The hole's margin around its button, px. */
const MARGIN = 7;
/** How far the card sits from the button, past the hand. */
const CARD_GAP = 78;

export function spotlight(
  target: HTMLElement | null,
  words: LessonWords,
  onTarget: () => void,
  frame: HTMLElement | null = null,
): Spotlight {
  const root = el('div', { class: 'spotlight', 'data-target': String(target !== null) });
  const hole = el('div', { class: 'spotlight-hole', 'aria-hidden': 'true' });
  const hand = el('div', { class: 'spotlight-hand', 'aria-hidden': 'true' }, [
    el('span', { class: 'spotlight-ripple' }),
    handSvg(),
  ]);
  const card = el('div', { class: 'spotlight-card', role: 'dialog', 'aria-live': 'polite' }, [
    el('div', { class: 'spotlight-kicker', text: words.kicker }),
    el('div', { class: 'spotlight-line', text: words.line }),
    el('div', { class: 'spotlight-todo', text: words.todo }),
  ]);
  if (words.button) {
    const button = el('button', { class: 'primary', type: 'button' }, [words.button]);
    button.addEventListener('click', (event) => {
      event.stopPropagation();
      onTarget();
    });
    card.append(button);
  }
  if (target) root.append(hole, hand);
  root.append(card);

  // Every tap lands here first; only one inside the hole reaches the button.
  root.addEventListener('click', (event) => {
    if (!target) return;
    const box = target.getBoundingClientRect();
    const inside =
      event.clientX >= box.left - MARGIN &&
      event.clientX <= box.right + MARGIN &&
      event.clientY >= box.top - MARGIN &&
      event.clientY <= box.bottom + MARGIN;
    if (inside) onTarget();
  });

  const place = (): void => {
    const height = window.innerHeight;
    if (!target) {
      card.style.top = `${Math.max(16, height / 2 - card.offsetHeight / 2)}px`;
      card.style.bottom = '';
      return;
    }
    const box = target.getBoundingClientRect();
    const lit = (frame ?? target).getBoundingClientRect();
    hole.style.left = `${lit.left - MARGIN}px`;
    hole.style.top = `${lit.top - MARGIN}px`;
    hole.style.width = `${lit.width + MARGIN * 2}px`;
    hole.style.height = `${lit.height + MARGIN * 2}px`;
    // The hand comes up from below where there is room for it, else down from above.
    const below = box.bottom + CARD_GAP < height;
    hand.dataset.from = below ? 'below' : 'above';
    hand.style.left = `${box.left + box.width / 2}px`;
    hand.style.top = `${below ? box.bottom - 4 : box.top + 4}px`;
    // The card over what is lit if it fits there, else past the hand.
    const cardHeight = card.offsetHeight;
    let top: number | null = null;
    if (frame && lit.top - MARGIN - 12 - cardHeight >= 16) top = lit.top - MARGIN - 12 - cardHeight;
    else if (below) top = Math.min(height - cardHeight - 16, box.bottom + CARD_GAP);
    if (top !== null) {
      card.style.bottom = '';
      card.style.top = `${top}px`;
    } else {
      card.style.top = '';
      card.style.bottom = `${Math.max(16, height - box.top + CARD_GAP)}px`;
    }
  };

  document.body.append(root);
  place();
  // The layout settles over the first frames (fonts, a scroll into view): follow it.
  let frames = 0;
  const follow = (): void => {
    if (!root.isConnected || frames > 40) return;
    frames += 1;
    place();
    requestAnimationFrame(follow);
  };
  requestAnimationFrame(follow);
  window.addEventListener('resize', place);

  return {
    place,
    remove: () => {
      window.removeEventListener('resize', place);
      root.remove();
    },
  };
}

/**
 * The guided shifts' gloved hand, index finger up, its tip at (0, 0): thumb,
 * palm with the folded fingers, the pointing finger over them, and a cuff. The
 * ripple where it touches is its own element, so it stays while the hand lifts.
 */
function handSvg(): SVGSVGElement {
  const svg = document.createElementNS(SVG, 'svg');
  svg.setAttribute('viewBox', '-16 -2 38 54');
  svg.setAttribute('class', 'spotlight-glove');
  svg.innerHTML =
    '<ellipse cx="-9" cy="31" rx="5" ry="8"/>' +
    '<rect x="-7" y="17" width="25" height="27" rx="9"/>' +
    '<path d="M5 19v6M10 19v6M15 19v6" stroke-width="1.2"/>' +
    '<rect x="-5" y="0" width="10" height="28" rx="5"/>' +
    '<rect class="spotlight-cuff" x="-7" y="42" width="25" height="6" rx="3"/>';
  return svg;
}
